import {
  EActionType,
  UserAssignmentsScope,
  IdFormat,
  PriorityValue,
  ESessionStore,
  EVariableType,
  EPageNames,
  SUBMODEL_PREFIX,
  EConfigitProperty,
  EJustification,
} from '../../data/Constants';
import {
  createVariableAssignments,
  getConflict,
  addPdmProperties,
  isValidConfigurableSection,
  isSubmodelCountVariable,
  getValueById,
  IsAppOpenedByExtApp,
  IsAppOpenedInIframe,
  getVariableType,
  getPropertyValue,
  getFeatureId,
  isRestrictedState,
} from '../../services';
import { SessionStore } from '../../services/SessionStore';
import { getLower, getUpper } from '../../services/NumericHelperFunctions';
import { appSettings } from '../../settings';
import { IsDefaultFlowInSessionStore } from '../../services/SessionStoreHelperFunctions';
import { TFunction } from 'i18next';

import type {
  ILoadConfiguration,
  ILoadConfigurationPayload,
  IExtendedConfigureResponseOptional,
  IExtendedConfigureResponse,
  IContextDataCountMap,
  IConfiguration,
  IContextData,
  ISection,
  IConflict,
  IAssignment,
  IContextDataModel,
  IVariableAssignment,
  IImagesData,
  IMaterialPropertyAssociations,
  IMaterialImages,
  IFilteredBundleRules,
  IConfigurationVariable,
  IBundleRule,
  IIbdAssignedCodes,
  ISelectedBundles,
  IOptionalNumericValue,
  IConfigurationValue,
  INumericFallbackEntry,
  ILastChange,
} from '../../../types';
import type {
  IError,
  IConfigurationAction,
  IConfigureResult,
  IImageResponse,
  IPropertyAssociationResponse,
  ILoadConfigurationResult,
  IBundleRuleResponse,
} from '../../../types/IApiTypes';

/**
 * Converts the raw `optionalItems` string array from the API into a typed Map for in-memory use.
 *
 * Each entry in the array follows one of two formats:
 *   - `"id;value"` — an optional numeric/string variable with a persisted value.
 *   - `"id"`       — a plain optional item (e.g. a feature/option) with no embedded value.
 *
 * @param {ILoadConfigurationPayload} data - The raw payload received from the load-configuration API.
 * @returns {ILoadConfiguration} The same data with `optionalItems` replaced by a typed Map.
 *
 * @example
 * // Input from API:
 * // optionalItems: ["NM1;10", "NM2;5", "FF2"]
 *
 * // Resulting Map:
 * // { "NM1" => "10", "NM2" => "5", "FF2" => true }
 */
const createOptionalItems = (data: ILoadConfigurationPayload): ILoadConfiguration => {
  const optionalItems = new Map<string, boolean | IOptionalNumericValue>();
  if (Array.isArray(data.optionalItems)) {
    for (const item of data.optionalItems) {
      const sep = item.indexOf(';');
      if (sep > -1) {
        optionalItems.set(item.slice(0, sep), {
          value: item.slice(sep + 1),
          assigned: EJustification.User,
        });
      } else {
        optionalItems.set(item, true);
      }
    }
  }
  return { ...data, optionalItems };
};

/**
 * Returns the minimum valid value when a rule raises the range minimum (making singleton 0 incompatible)
 * and no value is currently assigned. Returns null if anything is already assigned or no values are incompatible.
 * @param {IConfigurationVariable} variable - The numeric variable to inspect.
 * @returns {string | null} The minimum of the non-incompatible values as a string, or null.
 */
function getIncompatibleNumericFallbackValue(
  variable: IConfigurationVariable
): { value: string; lower: number; upper: number } | null {
  // If a value is already assigned (by user or engine), no fallback needed.
  const hasAssigned = variable.values?.some((v) => v.assigned);
  if (hasAssigned) return null;
  // Only Available values are selectable; no Available values means the rule hasn't opened a range yet.
  const validValues = variable.values.filter((v) => v.state === 'Available');
  if (!validValues.length) return null;
  const lower = getLower(validValues);
  const upper = getUpper(validValues);

  if (lower === 0) return null; // Zero is not a meaningful fallback; skip to avoid spurious auto-assignments.

  // Store lower/upper so the merge step can detect if the rule context changed later.
  return { value: String(lower), lower, upper };
}

/**
 * Returns true when `value` falls inside any `Available` entry of the variable's ranges
 * (either a matching discrete value or within a range's [lower, upper]).
 */
function isNumericValueAvailable(variable: IConfigurationVariable, value: number): boolean {
  if (Number.isNaN(value)) return false;
  return variable.values.some((v) => {
    if (v.state !== 'Available') return false;
    if ('name' in v) return Number(v.value) === value;
    return Number(v.lower) <= value && value <= Number(v.upper);
  });
}

/**
 * Applies numeric-fallback logic for one variable:
 *   - If the variable is currently optional and its stored value is outside the new Available range,
 *     rewrites the stored value to `fallback` and records the id in `invalidOptionalVariable`
 *     so the outer useEffect persists the mutated Map via `setOptionalItemsApi`.
 *   - Otherwise records `fallback` in `numericFallbackAssignments` for later auto-assign.
 */
function handleNumericFallback(
  variable: IConfigurationVariable,
  numericFallbackAssignments: Record<string, INumericFallbackEntry>,
  invalidOptionalVariable: string[],
  optionalItems?: Map<string, boolean | IOptionalNumericValue>
): void {
  const fallback = getIncompatibleNumericFallbackValue(variable);
  // null means variable already has a value or no Available range — nothing to do.
  if (fallback === null) return;

  if (!optionalItems?.has(variable.id)) {
    // Non-optional variable: queue for auto-assign via the fallback effect in Configurator.
    // isUserDeselected starts false; the merge step in configured() may flip it to true if the user cleared it.
    numericFallbackAssignments[variable.id] = { ...fallback, isUserDeselected: false };
    return;
  }

  const optionalItem = optionalItems.get(variable.id);
  if (typeof optionalItem !== 'object' || optionalItem === null) return;
  // Optional variable whose stored value is still within the Available range — no correction needed.
  if (isNumericValueAvailable(variable, Number(optionalItem.value))) return;

  // Stored optional value is outside the new range — clamp it to the new minimum and persist via setOptionalItemsApi.
  optionalItem.value = fallback.value;
  invalidOptionalVariable.push(variable.id);
}

/**
 * Extracts data from the configure response and returns the load configuration result to the slice.
 * @param {IConfigurationAction} action - The configuration action.
 * @param {IExtendedConfigureResponse} state - The current configuration state.
 * @returns {ILoadConfigurationResult} The load configuration result including selectedBundles and ibdAssignedCodes.
 */

/**
 * Merges fresh traversal results with previous Redux state to produce the next
 * numericFallbackAssignments map.
 *
 * - Same range → keep existing entry (preserves isUserDeselected across configure calls).
 * - Range changed or new variable → use fresh entry (resets isUserDeselected; new rule context).
 * - Variables absent from fresh are dropped (they gained a value and no longer need auto-assign).
 * - Deselect guard: if lastChange cleared a fallback variable, mark it isUserDeselected:true.
 */
function buildNumericFallbackMap(
  fresh: Record<string, INumericFallbackEntry>,
  previous: Record<string, INumericFallbackEntry>,
  lastChange?: ILastChange
): Record<string, INumericFallbackEntry> {
  const merged: Record<string, INumericFallbackEntry> = {};
  for (const [id, freshEntry] of Object.entries(fresh)) {
    const existing = previous[id];
    merged[id] =
      existing?.lower === freshEntry.lower && existing?.upper === freshEntry.upper
        ? existing
        : freshEntry;
  }
  if (lastChange?.value === '' && lastChange.variableId && merged[lastChange.variableId]) {
    merged[lastChange.variableId] = { ...merged[lastChange.variableId], isUserDeselected: true };
  }
  return merged;
}

export const loadedConfiguration = (
  action: IConfigurationAction,
  state: IExtendedConfigureResponse
): ILoadConfigurationResult => {
  if (action.data) {
    action.data = createOptionalItems(action.data);
  }
  const variableAssignments = createVariableAssignments(action.data.userAssignments);
  const newState: IExtendedConfigureResponse = {
    ...state,
    savedConfiguration: action.data,
    contextData: getContextData(action.data),
    assignments: variableAssignments,
    access: action.access,
  };
  const data = configured(action, newState);
  return {
    savedConfiguration: action.data,
    contextData: data?.contextData,
    access: action.access,
    conflict: data?.conflict,
    data: data?.data,
    assignments: data?.assignments,
    isInvalid: data && (data.isInvalid === false || data.isInvalid) ? data.isInvalid : null,
    acceptedChanges: data?.acceptedChanges,
    selectedBundles: data?.selectedBundles ?? {},
    ibdAssignedCodes: data?.ibdAssignedCodes ?? {},
    numericFallbackAssignments: data?.numericFallbackAssignments ?? {},
    invalidOptionalVariable: data.invalidOptionalVariable,
  };
};

/**
 * This function extract the data from configure response and updated to the state variables
 * @param {IConfigurationaction } action the configuration action
 * @param {IExtendedConfigureResponse} state the configuration state
 * @returns {IExtendedConfigureResponse} updated the the configuration state
 */

export const configured = (
  action: IConfigurationAction,
  state: IExtendedConfigureResponse
): IConfigureResult | IExtendedConfigureResponse => {
  action.configuration = action.configuration ?? action.data.configureResponse ?? undefined;

  if (!action.configuration) {
    return state;
  }

  const assignmentsToRemove = action.configuration.removedAssignments?.variableAssignments;
  let assignments = state.assignments ? [...state.assignments] : [];
  if (action.apiType === EActionType.ClearAll) {
    assignments = assignments.filter((assignedmentVal) =>
      UserAssignmentsScope.find((userVal) => userVal === assignedmentVal.variableId)
    ); //Update UserAssignmentsScope in Constant file if new static id adds up
  }

  const conflict =
    state.isInvalid === false ? getConflict({ ...state, data: { ...action.configuration } }) : null;

  const shortSalesText = state.shortSalesText;

  if (assignmentsToRemove) {
    assignments = removeAssignments(assignmentsToRemove, shortSalesText, assignments);
  }
  const args = action.configuration.arguments?.Configuration || {};
  const submodels = Object.keys(args).filter((e) => {
    return e.endsWith(IdFormat.Suffix.SubmodelId) && args[e] >= 1;
  });
  submodels.forEach((m) => {
    const ass = assignments.find((a) => a.variableId === m);
    if (!ass) {
      assignments.push({
        type: 'Singleton',
        variableId: m,
        value: args[m].toString(),
        exclude: false,
        priority: PriorityValue.High,
      });
    } else if (Number(ass.value) >= 1) {
      ass['priority'] = PriorityValue.High;
    }
  });

  sanitizeConfiguration(action.configuration, state.contextData);

  // Seed countVariables with known IDs from the previous configure response
  // so we keep tracking variables that may be temporarily absent from this response.
  const countVariables: IContextDataCountMap = {};
  if (state.contextData?.countVariables) {
    Object.keys(state.contextData?.countVariables).forEach((k) => {
      countVariables[k] = null;
    });
  }

  // First pass: refresh count variable references, collect IBD codes, and record numeric
  // fallback values for incompatible variables. optionalItems are intentionally not consulted
  // here — bundle-driven variables get their optional-item override in the second pass below.
  const rootProductCode = state.savedConfiguration?.modelContext?.rootModel?.id ?? '';
  const freshFallbackAssignments: Record<string, INumericFallbackEntry> = {};
  const { assignedCodesPerProduct, invalidOptionalVariable } = collectSubModelVarsAndAssignedCodes(
    action.configuration.sections,
    countVariables,
    rootProductCode,
    state.savedConfiguration?.state ?? action.data.state,
    state.savedConfiguration?.optionalItems,
    freshFallbackAssignments
  );

  // Determine which bundle-driven feature codes are active per product.
  const selectedBundles = getSelectedBundles(
    assignedCodesPerProduct,
    state.bundleData ?? [],
    rootProductCode
  );

  // Second pass: for numeric variables that are driven by an active bundle rule AND have a
  // stored optional value, re-evaluate the optional value against the new Available range.
  // Only bundle-driven variables are eligible — non-bundle optional values are left intact.
  const optionalItems = state.savedConfiguration?.optionalItems;
  if (optionalItems) {
    handleBundleDrivenOptionalNumeric(
      action.configuration.sections,
      selectedBundles,
      freshFallbackAssignments,
      invalidOptionalVariable,
      optionalItems
    );
  }

  //Update the variable and value  configit properties using  content folder propert Associations response
  action.configuration.sections = addPdmProperties(
    action.configuration.sections,
    state.propertyAssociations,
    state.propertyDefinition
  );
  //remove once real properties are available
  // fixSubModelViews( action.configuration );
  const assignmentsBefore = state.assignments ? [...state.assignments] : [];

  const numericFallbackAssignments = buildNumericFallbackMap(
    freshFallbackAssignments,
    state.numericFallbackAssignments,
    action.lastChange
  );

  return {
    ...checkInvalid(
      {
        ...state,
        contextData: { ...state.contextData, countVariables: { ...countVariables } },
        selectedBundles: selectedBundles,
        ibdAssignedCodes: assignedCodesPerProduct,
      },
      action,
      conflict,
      assignments,
      assignmentsToRemove,
      assignmentsBefore
    ),
    numericFallbackAssignments,
    invalidOptionalVariable,
  };
};

const sanitizeConfiguration = (configuration: IConfiguration, contextData: IContextData | null) => {
  configuration.sections = configuration.sections?.filter((s) => sanitizeSection(contextData, s));
};

const sanitizeSection = (contextData: IContextData | null, section: ISection) => {
  section.sections = section.sections?.filter((s) => sanitizeSection(contextData, s));
  return isValidConfigurableSection(contextData, section);
};

const checkInvalid = (
  state: IExtendedConfigureResponseOptional,
  action: IConfigurationAction,
  conflict: IConflict | null,
  assignments: IAssignment[],
  assignmentsToRemove: IVariableAssignment[] | undefined,
  assignmentsBefore: IAssignment[]
): IConfigureResult => {
  if (state.isInvalid === null) {
    if (assignmentsToRemove?.length) {
      return {
        conflict,
        data: action.configuration,
        assignments,
        isInvalid: true,
        acceptedChanges: false,
        contextData: state.contextData,
        selectedBundles: state.selectedBundles ?? {},
        ibdAssignedCodes: state.ibdAssignedCodes ?? {},
        assignmentsBefore: assignmentsBefore,
      };
    } else {
      return {
        conflict,
        data: action.configuration,
        assignments,
        isInvalid: false,
        acceptedChanges: false,
        contextData: state.contextData,
        selectedBundles: state.selectedBundles ?? {},
        ibdAssignedCodes: state.ibdAssignedCodes ?? {},
        assignmentsBefore: assignmentsBefore,
      };
    }
  } else {
    if (state.isInvalid) {
      //If user accepts proposed changes
      return {
        conflict: null,
        data: action.configuration,
        assignments,
        isInvalid: false,
        acceptedChanges: true,
        contextData: state.contextData,
        selectedBundles: state.selectedBundles ?? {},
        ibdAssignedCodes: state.ibdAssignedCodes ?? {},
        assignmentsBefore: assignmentsBefore,
      };
    }
    return {
      conflict,
      data: action.configuration,
      assignments,
      isInvalid: state.isInvalid,
      contextData: state.contextData,
      selectedBundles: state.selectedBundles ?? {},
      ibdAssignedCodes: state.ibdAssignedCodes ?? {},
      assignmentsBefore: assignmentsBefore,
    };
  }
};

/**
 * Generates and returns the context data from model context
 * @param {ILoadConfiguration} data The data received from read api call
 * @returns {IContextData} context data which maps model id to model context
 */
function getContextData(data?: ILoadConfiguration): IContextData {
  if (!data) {
    return { models: undefined, countVariables: undefined };
  }
  const modelContext = data.modelContext;
  const { rootModel, subModels } = modelContext;
  const models: { [key: string]: IContextDataModel } = {
    [rootModel.id]: { ...rootModel, isRoot: true },
  };
  if (rootModel.id) {
    SessionStore.set(ESessionStore.ProductId, rootModel.id);
  }
  const countVariables: IContextDataCountMap = {};
  subModels.forEach((sm) => {
    models[sm.id] = sm;
    if (sm.subModelVariableIds?.length) {
      sm.subModelVariableIds?.forEach((v) => {
        countVariables[v] = null;
      });
    }
  });

  return { models, countVariables };
}

/**
 * Collects bundle-driving composite codes from a single variable into the per-product assigned codes array.
 * Only codes whose IBD (IsBundleDriving) Configit property is true are collected.
 *
 * Product scoping rules:
 *   - Variable ID contains 'I_submodelId' prefix  →  belongs to that submodel product.
 *   - Variable ID has no 'I_' prefix              →  belongs to the root product.
 *
 * Composite key format:
 *   - Singleselect / Multiselect:  "variableId.optionValue"  (e.g. 'FNA4884.NNAL591')
 *   - Number / Date / String:      full variable.id           (e.g. 'I_782407.FMR0553')
 *
 * Example:
 *   variable.id = 'FNA4884', val.value = 'NNAL591', IBD=true, assigned=true  →  codeSet gets 'FNA4884.NNAL591'
 *   variable.id = 'FNA4884', val.value = 'NNAL592', IBD=false, assigned=true →  nothing pushed (not bundle-driving)
 */
function collectAssignedIBDCodesFromVariable(
  variable: IConfigurationVariable,
  assignedCodesPerProduct: IIbdAssignedCodes,
  rootProductCode: string
): void {
  const submodelCode = extractSubmodelProductCode(variable.id);
  const productCode = submodelCode ?? rootProductCode;
  if (!assignedCodesPerProduct[productCode]) {
    assignedCodesPerProduct[productCode] = [];
  }
  const codeSet = assignedCodesPerProduct[productCode];
  const variableType = getVariableType(variable);
  if (
    variableType === EVariableType.Number ||
    variableType === EVariableType.Date ||
    variableType === EVariableType.String
  ) {
    if (
      (variable.properties?.find((p) => p.id === EConfigitProperty.IsBundleDriving)
        ?.value as boolean) &&
      variable.values?.some((val) => val.assigned)
    ) {
      codeSet.push(variable.id);
    }
  } else {
    variable.values.forEach((val) => {
      if (
        (val.properties?.find((p) => p.id === EConfigitProperty.IsBundleDriving)
          ?.value as boolean) &&
        val.assigned
      ) {
        codeSet.push(variable.id + IdFormat.CodeSeparator + String(val.value));
      }
    });
  }
}

/**
 * Single-pass traversal that refreshes count variable references AND collects
 * all currently assigned, bundle-driving composite codes grouped by product code.
 *
 * Only codes where IBD (IsBundleDriving) is true are included. The pre-filtered
 * arrays are stored in state as ibdAssignedCodes so getProductData.fulfilled
 * can call getSelectedBundles without re-traversing sections.
 *
 * Composite key format per entry:
 *   - Singleselect / Multiselect:  "variableId.optionValue"  (e.g. 'FNA4884.NNAL591')
 *   - Number / Date / String:      full variable.id           (e.g. 'I_782407.FMR0553')
 *
 * @param {ISection[]} sections - Sections from the configure response.
 * @param {IContextDataCountMap} countVariables - Map to populate with count variable references (mutated in place).
 * @param {string} rootProductCode - The root model product ID, used to scope variable ownership.
 * @returns {IIbdAssignedCodes} Map of productCode to array of IBD-true composite codes.
 *
 * Example output when NNAL591 (IBD=true) is assigned on root product '718135':
 *   { '718135': ['FNA4884.NNAL591'] }
 */
export function collectSubModelVarsAndAssignedCodes(
  sections: ISection[],
  countVariables: IContextDataCountMap,
  rootProductCode: string,
  state: string,
  optionalItems?: Map<string, boolean | IOptionalNumericValue>,
  numericFallbackAssignments: Record<string, INumericFallbackEntry> = {}
): { assignedCodesPerProduct: IIbdAssignedCodes; invalidOptionalVariable: string[] } {
  const assignedCodesPerProduct: IIbdAssignedCodes = {};
  const invalidOptionalVariable: string[] = [];
  const traverse = (secs: ISection[]) => {
    secs.forEach((section) => {
      section.variables.forEach((variable) => {
        if (variable.id in countVariables || isSubmodelCountVariable(variable)) {
          countVariables[variable.id] = variable;
        }
        collectAssignedIBDCodesFromVariable(variable, assignedCodesPerProduct, rootProductCode);
        if (variable.valueType === EVariableType.Number && !isRestrictedState(state)) {
          handleNumericFallback(
            variable,
            numericFallbackAssignments,
            invalidOptionalVariable,
            optionalItems
          );
        }
        collectInvalidOptionalItems(variable, invalidOptionalVariable, optionalItems);
      });
      if (section.sections?.length) traverse(section.sections);
    });
  };
  traverse(sections);
  return { assignedCodesPerProduct, invalidOptionalVariable };
}

function applyBundleDrivenNumericVariable(
  variable: IConfigurationVariable,
  selectedBundles: ISelectedBundles,
  numericFallbackAssignments: Record<string, INumericFallbackEntry>,
  invalidOptionalVariable: string[],
  optionalItems: Map<string, boolean | IOptionalNumericValue>
): void {
  if (variable.valueType !== EVariableType.Number) return;
  if (!optionalItems.has(variable.id)) return;

  const isBundleDriven = Object.values(selectedBundles).some((driven) =>
    driven.includes(variable.id)
  );
  if (!isBundleDriven) return;

  const optionalItem = optionalItems.get(variable.id) as IOptionalNumericValue;
  const validValues = variable.values.filter((v) => v.state === 'Available');
  // Bundle-driven optional numeric: move from optionalItems into the fallback map so the
  // fallback effect (not the optional-items flow) submits the configure call.
  // lower/upper are stored here so the merge step can detect rule context changes later.
  numericFallbackAssignments[variable.id] = {
    value: optionalItem.value,
    isUserDeselected: false,
    lower: getLower(validValues),
    upper: getUpper(validValues),
  };
  // Remove from optionalItems to prevent a duplicate configure call via the optional-items path.
  optionalItems.delete(variable.id);
  invalidOptionalVariable.push(variable.id);
}

/**
 * Second-pass traversal: for numeric variables that are bundle-driven AND stored in optionalItems,
 * forces the optional value into numericFallbackAssignments and removes the variable from optionalItems.
 * Non-bundle optional variables are left intact — their value was already reconciled by handleNumericFallback.
 */
function handleBundleDrivenOptionalNumeric(
  sections: ISection[],
  selectedBundles: ISelectedBundles,
  numericFallbackAssignments: Record<string, INumericFallbackEntry>,
  invalidOptionalVariable: string[],
  optionalItems: Map<string, boolean | IOptionalNumericValue>
): void {
  const traverse = (secs: ISection[]) => {
    secs.forEach((section) => {
      section.variables.forEach((variable) =>
        applyBundleDrivenNumericVariable(
          variable,
          selectedBundles,
          numericFallbackAssignments,
          invalidOptionalVariable,
          optionalItems
        )
      );
      if (section.sections?.length) traverse(section.sections);
    });
  };
  traverse(sections);
}

/**
 * Extracts the innermost submodel product code from a variable ID.
 * Returns null for root-model variables (no 'I_' segment in the path).
 *
 * Examples:
 *   'I_782407.FMR0553.NMRF476'  →  '782407'  (submodel)
 *   'I_PM1.I_PM2.FMR0553'       →  'PM2'     (innermost submodel wins)
 *   'FNA4884.NNAL591'            →  null      (root model variable)
 *   'FMR0560'                    →  null      (root model variable)
 */
export function extractSubmodelProductCode(variableId: string): string | null {
  if (!variableId?.includes(SUBMODEL_PREFIX)) return null;
  const parts = variableId.split('.');
  for (let i = parts.length - 1; i >= 0; i--) {
    if (parts[i].startsWith(SUBMODEL_PREFIX)) return parts[i].substring(SUBMODEL_PREFIX.length);
  }
  return null;
}

/**
 * Converts a dot-separated product path into a flat 'I_'-prefixed segment string used as a code prefix.
 * Strips the root product ID prefix if present, then maps each remaining segment
 * to its 'I_<segment>' form and joins them with underscore.
 *
 * @param {string} parentPath - The dot-separated product path (e.g. '718135.782407').
 * @param {string} rootProductCode - The root model product ID to strip from the start.
 * @returns {string} The flattened prefix string.
 *
 * Examples:
 *   appendProductPath('718135.782407', '718135')       → 'I_782407'
 *   appendProductPath('718135.782407.99901', '718135') → 'I_782407_I_99901'
 *   appendProductPath('782407', '718135')              → 'I_782407'
 */
function appendProductPath(parentPath: string, rootProductCode: string): string {
  const result = parentPath.startsWith(rootProductCode + IdFormat.CodeSeparator)
    ? parentPath.slice(rootProductCode.length + IdFormat.CodeSeparator.length)
    : parentPath;

  return result
    .split(IdFormat.CodeSeparator)
    .map((segment) => SUBMODEL_PREFIX + segment)
    .join('_');
}

/**
 * Recursively builds the selectedBundles map by walking bundle rules from a driving code
 * down to all transitively driven codes, including those nested inside submodels.
 *
 * For each driving code that has IBD=true and is currently assigned:
 *   - Non-submodel driven codes are added directly to result[drivingCode].
 *   - Submodel-prefixed driven codes (starting with 'I_') trigger a recursive call into
 *     that submodel's bundle group so that deeply nested driven codes are also resolved.
 *
 * @param {IBundleRule} bundle - The bundle rule to process (contains drivingCode and drivenCodes).
 * @param {string} currentProductPath - The dot-separated path from root to the current product
 *   (e.g. '718135' for root, '718135.782407' for a direct submodel).
 * @param {IFilteredBundleRules[]} bundleData - All bundle rule groups from the API.
 * @param {string} rootProductCode - The root model product ID.
 * @param {{ [drivingCode: string]: string[] }} result - Accumulator map mutated in place:
 *   drivingCodePath → array of composite driven codes.
 * @param {string[]} productFeaturesAssignedAndHasIBD - IBD-true composite codes for the current product.
 * @param {IIbdAssignedCodes} ibdAssignedCodes - Full map of all products'
 *   IBD-true codes, passed through to recursive calls so each submodel level uses its own codes.
 * @returns {void}
 */
function createNestedBundleMappings(
  bundle: IBundleRule,
  currentProductPath: string,
  bundleData: IFilteredBundleRules[],
  rootProductCode: string,
  result: { [drivingCode: string]: string[] },
  productFeaturesAssignedAndHasIBD: string[],
  ibdAssignedCodes: IIbdAssignedCodes
): void {
  // Build the fully-qualified driving code key by prepending the full product path.
  // e.g. root:    '782157' + '.' + 'FNA9921.NNAK143'  → '782157.FNA9921.NNAK143'
  //      submodel:'782157.782301' + '.' + 'FMR0468.NMRB978' → '782157.782301.FMR0468.NMRB978'
  const drivingCode = currentProductPath + IdFormat.CodeSeparator + bundle.drivingCode;

  // Compute the 'I_'-prefixed code prefix used to look up entries in ibdAssignedCodes.
  // Root product yields '' (no prefix needed); a submodel path is flattened:
  //   appendProductPath('782157.782301', '782157') → 'I_782301'
  //   appendProductPath('782157.782301.782302', '782157') → 'I_782301_I_782302'
  const appendedProductPath =
    currentProductPath === rootProductCode
      ? ''
      : appendProductPath(currentProductPath, rootProductCode);

  // Verify the driving code is actually assigned with IBD=true before proceeding.
  // For root product (appendedProductPath=''):    check includes('FNA9921.NNAK143')
  // For submodel '782301' (appendedProductPath='I_782301'): check includes('I_782301.FMR0468.NMRB978')
  if (
    !productFeaturesAssignedAndHasIBD.includes(
      appendedProductPath
        ? appendedProductPath + IdFormat.CodeSeparator + bundle.drivingCode
        : bundle.drivingCode
    )
  ) {
    return;
  }

  // Guard: only initialise this driving code entry once to prevent duplicate processing.
  if (!result[drivingCode]) {
    result[drivingCode] = [];

    // Map each driven code to its composite form and store it under this driving code.
    bundle.drivenCodes.forEach((drivenCode) => {
      if (drivenCode.startsWith(SUBMODEL_PREFIX)) {
        // Submodel-prefixed driven code — this code belongs to a child submodel and
        // may itself be a driving code inside that submodel (transitive chain).
        // e.g. drivenCode = 'I_782301.FMR0468.NMRB978' means product '782301', code 'FMR0468.NMRB978'

        // Extract the child submodel product ID: 'I_782301.FMR0468.NMRB978' → '782301'
        const submodelCode = extractSubmodelProductCode(drivenCode);

        // Strip the 'I_<id>.' prefix to get the inner code: 'I_782301.FMR0468.NMRB978' → 'FMR0468.NMRB978'
        const possibelDrivingCode = drivenCode.substring(drivenCode.indexOf('.') + 1);

        // Extend the product path one level deeper into the submodel:
        // '782157' + '.' + '782301' → '782157.782301'
        const nextProductPath = currentProductPath + IdFormat.CodeSeparator + submodelCode;

        // Store the flattened composite code for price/isBundled stamping:
        // appendProductPath('782157.782301', '782157') + '.' + 'FMR0468.NMRB978' → 'I_782301.FMR0468.NMRB978'
        result[drivingCode].push(
          appendProductPath(nextProductPath, rootProductCode) +
            IdFormat.CodeSeparator +
            possibelDrivingCode
        );

        // Look up the submodel's bundle group to check for a transitive chain.
        // e.g. find group with productCode '782301'
        const drivenGroup = bundleData.find((x) => x.productCode === submodelCode);

        // No bundle rules defined for submodel '782301' — transitive chain ends here.
        if (!drivenGroup) {
          return;
        }

        // Check if 'FMR0468.NMRB978' is also a driving code inside submodel '782301'.
        // If found, its driven codes ['FAE0057.NAEA152', 'FAE0058.NAEA151', 'I_782302.FMR0469.NMRB979']
        // will be resolved in the recursive call.
        const drivenBundle = drivenGroup.bundles?.find(
          (b) => b.drivingCode === possibelDrivingCode
        );

        // 'FMR0468.NMRB978' is not a driving code in submodel '782301' — chain ends here.
        if (!drivenBundle) {
          return;
        }

        // Recurse one level deeper: resolve driven codes for submodel '782301' starting at
        // currentProductPath '782157.782301', using that submodel's own IBD-true codes.
        createNestedBundleMappings(
          drivenBundle,
          currentProductPath + IdFormat.CodeSeparator + submodelCode,
          bundleData,
          rootProductCode,
          result,
          ibdAssignedCodes[submodelCode as string] ?? [],
          ibdAssignedCodes
        );
      } else {
        // Plain driven code — belongs to the same product context as the driving code.
        // Root context (appendedProductPath=''):       push 'FAE0059.NAEA150' as-is
        // Submodel context (appendedProductPath='I_782301'): push 'I_782301.FAE0057.NAEA152'
        result[drivingCode].push(
          appendedProductPath
            ? appendedProductPath + IdFormat.CodeSeparator + drivenCode
            : drivenCode
        );
      }
    });
  }
}

/**
 * Evaluates bundle rules against assigned IBD codes and returns a flat map of active driven codes
 * keyed by their fully-qualified driving code path.
 *
 * Delegates to createNestedBundleMappings for each bundle rule to resolve transitively driven
 * codes, including those nested inside submodel products.
 *
 * @param {IIbdAssignedCodes} ibdAssignedCodes - Map of productCode to
 *   composite codes that are assigned AND have IBD=true.
 * @param {IFilteredBundleRules[]} bundleData - Bundle rule groups from the API.
 * @param {string} rootProductCode - The root model product ID.
 * @returns {ISelectedBundles} Map of fully-qualified driving code path
 *   to an array of driven composite codes active under that driving code.
 *
 * Example:
 *   ibdAssignedCodes: { '718135': ['FNA4884.NNAL591'] }
 *   bundleData: [{ productCode: '718135', bundles: [{ drivingCode: 'FNA4884.NNAL591', drivenCodes: ['989801296553'] }] }]
 *   rootProductCode: '718135'
 *
 *   Result:
 *     { '718135.FNA4884.NNAL591': ['989801296553'] }
 */
export function getSelectedBundles(
  ibdAssignedCodes: IIbdAssignedCodes,
  bundleData: IFilteredBundleRules[],
  rootProductCode: string
): ISelectedBundles {
  // Accumulates drivingCodePath → [drivenCodes] for every active bundle rule across all products.
  const result: ISelectedBundles = {};

  // Iterate over each product's bundle group, e.g.
  //   { productCode: '782157', bundles: [{ drivingCode: 'FNA9921.NNAK143', drivenCodes: [...] }] }
  //   { productCode: '782301', bundles: [{ drivingCode: 'FMR0468.NMRB978', drivenCodes: [...] }] }
  bundleData.forEach((group) => {
    // Retrieve the IBD-true composite codes assigned for this product.
    // e.g. for '782157': ['FNA9921.NNAK143']; for '782301': ['I_782301.FMR0468.NMRB978']
    const productFeaturesAssignedAndHasIBD = ibdAssignedCodes[group.productCode];

    // No IBD-true assignments for this product — skip all its bundle rules.
    // e.g. skip '888002' entirely if the user has not assigned 'FNA9921.NNAK142' with IBD=true.
    if (!productFeaturesAssignedAndHasIBD) return;

    // Process each bundle rule within this product group.
    (group.bundles ?? []).forEach((bundle) => {
      // Build the full dot-separated path from root to this product.
      // Root '782157' → productPath = '782157'
      // Submodel '782301' under root '782157' → productPath = '782157.782301'
      // Submodel '782302' under '782301' under '782157' → productPath = '782157.782301.782302'
      const productPath =
        group.productCode === rootProductCode
          ? rootProductCode
          : rootProductCode + IdFormat.CodeSeparator + group.productCode;

      // Recursively resolve this rule's driving code and all transitively driven codes.
      // e.g. for root '782157' + rule 'FNA9921.NNAK143':
      //   result['782157.FNA9921.NNAK143'] = ['FAE0059.NAEA150', 'I_782301.FMR0468.NMRB978']
      createNestedBundleMappings(
        bundle,
        productPath,
        bundleData,
        rootProductCode,
        result,
        productFeaturesAssignedAndHasIBD,
        ibdAssignedCodes
      );
    });
  });
  return result;
}

/**
 * This function remove the conflicting assignments from the existing assignemnts
 * @param {IVariableAssignment[]} assignmentsToRemove remove assignemnts
 * @param {Map<string, Map<string, string>>} shortSalesText short sales text data
 * @param { IAssignment[] } assignments current assignemnts
 * @returns {IAssignment[]} return the assignments
 */

const removeAssignments = (
  assignmentsToRemove: IVariableAssignment[],
  shortSalesText: Map<string, Map<string, string>>,
  assignments: IAssignment[]
): IAssignment[] => {
  const validAssignments = [...assignments];
  for (const assignmentToRemove of assignmentsToRemove) {
    if (appSettings.UseShortSalesText && shortSalesText) {
      //Assign short sales text for variable
      const variableText = getValueById(shortSalesText, assignmentToRemove.variable.id);
      assignmentToRemove.variable.shortSalesText = variableText?.toString();

      //Assign short sales text for variable value
      const valueText = getValueById(
        shortSalesText,
        assignmentToRemove.value.value?.toString() ?? ''
      );
      assignmentToRemove.value.shortSalesText = valueText?.toString();
    }

    let index = -1;
    //Compare the feature value name for Numeric features and rest all type features compare the value
    if (assignmentToRemove.variable.valueType === EVariableType.Number) {
      index = validAssignments.findIndex(
        (a) =>
          a.variableId === assignmentToRemove.variable.id &&
          a.value === assignmentToRemove.value.name
      );
    } else {
      index = validAssignments.findIndex(
        (a) =>
          a.variableId === assignmentToRemove.variable.id &&
          a.value === assignmentToRemove.value.value
      );
    }
    if (index >= 0) {
      validAssignments.splice(index, 1);
    }
  }
  return validAssignments;
};

/**
 * This function set the current  price  to the state
 * @param { IImageResponse } action api payload
 * @param { IExtendedConfigureResponse } state configuration state
 * @returns { void }
 */

export function setProductImages(action: IImageResponse, state: IExtendedConfigureResponse): void {
  action.productImages =
    action.productImages && typeof action.productImages === 'string'
      ? JSON.parse(action.productImages)
      : action.productImages;

  const updateProductImages = new Map<string, IImagesData>(
    state.productImages?.get(action.modelId)
  );
  if (action.modelId && action.productImages) {
    action.productImages?.forEach((data: IImagesData) => {
      if (data.materialCode && !(data.materialCode in updateProductImages)) {
        updateProductImages.set(data.materialCode, data);
      }
    });

    state.productImages?.set(action.modelId, updateProductImages);
  }
}

/**
 * This function set the current  property associations   to the state
 * @param { IPropertyAssociationResponse } action api payload
 * @param { IExtendedConfigureResponseOptional } state configuration state
 * @returns { void }
 */

export function setPropertyAssociations(
  action: IPropertyAssociationResponse,
  state: IExtendedConfigureResponseOptional
): void {
  if (action.modelId) {
    const propertyAssociations = new Map<string, IMaterialPropertyAssociations>(
      state.propertyAssociations?.get(action.modelId)
    );
    if (action.propertiesAssociation) {
      action.propertiesAssociation?.forEach((data: IMaterialPropertyAssociations) => {
        if (data.materialCode && !(data.materialCode in propertyAssociations)) {
          propertyAssociations.set(data.materialCode, data);
        }
      });
    }
    state.propertyAssociations?.set(action.modelId, propertyAssociations);
  }
}

export const getErrorData = (t: TFunction): IError => {
  const isAppAccessedByEstore =
    (IsAppOpenedByExtApp() || IsAppOpenedInIframe()) && !IsDefaultFlowInSessionStore();
  const errorMessage = isAppAccessedByEstore
    ? 'errorMessages.EStore.ErrorMessage'
    : 'errorMessages.CPQ.ErrorMessage';
  return { response: null, code: 404, message: t(errorMessage), page: EPageNames.Configurator };
};

/**
 * Updates Sales text, ordering instructions and images for submodels in the configuration state
 * @param {IExtendedConfigureResponse} state - The configuration state
 * @param {string} language - The language code for sales text
 * @param {boolean} updateAllData - Flag to indicate whether to update all data i.e. ORI and images
 * @returns {void}
 */
export const updateSubmodelDataInState = (
  state: IExtendedConfigureResponse,
  language: string,
  updateAllData: boolean = false
) => {
  if (state.savedConfiguration) {
    const { rootModel } = state.savedConfiguration.modelContext;
    const submodelData = state.subModelData.get(rootModel.id);
    submodelData?.forEach((data) => {
      data.texts.forEach((text) => {
        if (text.id === language) {
          state.longSalesText?.set(data.id, new Map([[data.id, text.long]]));
          state.shortSalesText?.set(data.id, new Map([[data.id, text.short]]));
        }
      });
      if (updateAllData) {
        state.orderingInstructions?.set(data.id, data.ori);
        const imagesData: IMaterialImages[] = [];
        data?.images?.forEach((image: string) => {
          const imageData: IMaterialImages = {
            url: image,
            isDefault: true,
          };
          imagesData.push(imageData);
        });
        const imageData = {
          modelId: data.id,
          productImages: [
            {
              materialCode: data.id,
              materialImages: imagesData,
            },
          ],
        };
        setProductImages(imageData, state);
      }
    });
  }
};

export const filterBundleBasedOnCountry = (
  bundleGroups: IBundleRuleResponse[],
  country: string
): IFilteredBundleRules[] => {
  const filterdBundleBasedOnCountry = bundleGroups
    .map((group) => {
      const filteredBundles = (group.bundles ?? []).filter(
        (rule) => !rule.countries?.length || rule.countries.includes(country)
      );

      const bundleMap = new Map<string, IBundleRule>();

      filteredBundles.forEach((bundle) => {
        const existing = bundleMap.get(bundle.drivingCode);

        if (!existing) {
          bundleMap.set(bundle.drivingCode, {
            ...bundle,
            drivenCodes: [...new Set(bundle.drivenCodes ?? [])].filter(
              (code) => !code.endsWith(IdFormat.Suffix.SubmodelId)
            ), // Filter out submodel IDs from drivenCodes
          });
          return;
        }

        existing.drivenCodes = [
          ...new Set(
            [...(existing.drivenCodes ?? []), ...(bundle.drivenCodes ?? [])].filter(
              (code) => !code.endsWith(IdFormat.Suffix.SubmodelId)
            )
          ),
        ];
      });

      return {
        productCode: group.productCode,
        bundles: [...bundleMap.values()],
      };
    })
    .filter((group) => group.bundles.length > 0);
  return filterdBundleBasedOnCountry;
};

const checkIfOptionalIsSelected = (
  variable: IConfigurationVariable,
  featureOrOptionId: string,
  optionalItems?: Map<string, IOptionalNumericValue | boolean>,
  isPasswordProtected?: boolean
): boolean => {
  const variableType = getVariableType(variable);

  let value: IConfigurationValue | undefined;
  if (variableType === EVariableType.Number || variableType === EVariableType.String) {
    value = variable.values.find(
      (value: IConfigurationValue) => value.value && value.assigned && value.value !== 0
    );
  } else {
    // For VariableType other than Number / String like MultiValued, SingleValued, MultiNumeric
    value = variable?.values?.find((v) => v.value === featureOrOptionId);
  }

  if (
    value &&
    variableType === EVariableType.Number &&
    optionalItems?.has(variable.id) &&
    value.assigned !== 'ByRule'
  ) {
    return false;
  }
  if ((value || isPasswordProtected) && optionalItems?.has(featureOrOptionId)) {
    return true;
  }
  return false;
};

/**
 * Removes optional item selections when they become invalid.
 * Ensures that a feature cannot be both assigned and marked as optional simultaneously.
 * Handles special cases like password-protected features.
 * @param {IConfigurationVariable} variable - The variable to validate.
 * @returns {void}
 */
export const collectInvalidOptionalItems = (
  variable: IConfigurationVariable,
  invalidOptionalVariable: string[],
  optionalItems?: Map<string, IOptionalNumericValue | boolean>
): void => {
  variable.values.forEach((val) => {
    const isPasswordProtected = getPropertyValue<boolean>(
      variable,
      EConfigitProperty.IsPasswordProtected,
      EVariableType.Boolean
    );
    const featureOrOptionId = getFeatureId(val.value, variable);
    if (
      (checkIfOptionalIsSelected(variable, featureOrOptionId, optionalItems, isPasswordProtected) ||
        isPasswordProtected) &&
      optionalItems?.has(featureOrOptionId)
    ) {
      optionalItems.delete(featureOrOptionId);
      invalidOptionalVariable.push(featureOrOptionId);
    }
  });
};
